import { Image } from 'antd';
import { useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import { ImageCounterCenterBottom } from '../../../../components/common/counter/ImageCounterCenterBottom';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusType } from '../../../../constants/product/product.constant';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IProductImage } from '../../../../interfaces/Product.interfaces';
import { ProductImageOverlay } from './ProductImageOverlay';

interface ProductImageSectionProps {
  images: IProductImage[];
  status: ProductStatusType;
  pageType: 'odp' | 'pdp' | 'new-product';
  productTitle: string;
  productCode: string;
  onProductImageClick?: () => void;
  disableLoginPrompt?: boolean;
}

export const ProductImageSection = ({
  images,
  status,
  pageType,
  productTitle,
  productCode,
  onProductImageClick,
  disableLoginPrompt = false,
}: ProductImageSectionProps) => {
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const handleProductImageClick = () => {
    if (pageType === 'new-product') {
      onProductImageClick?.();
    } else {
      ReactGA.event('pvm_click_pdp_product_photo', {
        action: 'click',
        category: 'product_image_view',
        user_type: isUserLoggedIn ? 'member' : 'nonmember',
        page_type: pageType,
        product_title: productTitle,
      });
      ampli.pvmClickPdpProductPhoto({
        user_type: isUserLoggedIn ? 'member' : 'nonmember',
        page_type: pageType,
        product_title: productTitle,
        product_code: productCode,
      });
    }

    if (!isUserLoggedIn && pageType === 'odp' && !disableLoginPrompt) {
      ReactGA.event('pvm_click_login_button', {
        isModal: false,
      });
    }

    if (!isUserLoggedIn && !disableLoginPrompt) {
      navigate(pageInfo.loginPage.path);
    }
  };

  if (images && images.length > 0) {
    return (
      <SwiperWrapper id={'prod-pictures'}>
        <Swiper
          slidesPerView={1}
          onSlideChange={(slide) => {
            ampli.pvmSwipeProductPhoto({
              user_type: isUserLoggedIn ? 'member' : 'nonmember',
              page_type: pageType,
              product_title: productTitle,
              product_code: productCode,
              direction: slide.swipeDirection,
              image_number: `${slide.previousIndex}->${slide.realIndex}`,
            });
            console.log('test slide', slide);
            setActiveSlideImageIndex(slide.realIndex);
          }}
          edgeSwipeDetection
          loop
        >
          {images
            .sort((a, b) => {
              if (!a.order || !b.order) {
                return 0;
              }
              return a.order - b.order;
            })
            .filter((slideImage) => slideImage.type !== 'LIST')
            .map((slideImage, slideImageIndex) => {
              return (
                <CustomSwiperSlide key={slideImage.id}>
                  <ProductImageOverlay onClick={handleProductImageClick} disableLoginPrompt={disableLoginPrompt} />
                  <Image.PreviewGroup
                    preview={{
                      onChange: (current, prev) => {
                        ampli.pvmClickProductFullscreenPhotoArrowButton({
                          user_type: isUserLoggedIn ? 'member' : 'nonmember',
                          page_type: pageType,
                          product_title: productTitle,
                          product_code: productCode,
                          direction: current < prev ? 'prev' : 'next',
                          image_number: `${prev}->${current}`,
                        });
                      },
                      onVisibleChange: (visible: boolean) => {
                        if (visible) {
                          handleProductImageClick();
                        }
                      },
                      toolbarRender: () => null,
                    }}
                  >
                    {images
                      .sort((a, b) => {
                        if (!a.order || !b.order) {
                          return 0;
                        }
                        return a.order - b.order;
                      })
                      .filter((previewImage) => previewImage.type !== 'LIST')
                      .map((previewImage, previewImageIndex) => {
                        return (
                          <Image
                            key={previewImage.imageUrl}
                            src={previewImage.imageUrl}
                            alt={previewImage.imageUrl}
                            style={{
                              width: slideImageIndex === previewImageIndex ? '100%' : '0px',
                              height: slideImageIndex === previewImageIndex ? '100%' : '0px',
                              left: '0',
                              right: '0',
                              objectFit: 'cover',
                            }}
                            preview={{
                              mask: '',
                              onTransform(info) {
                                console.log('test info', info);
                              },
                              onVisibleChange: (value: boolean) => {
                                if (value === true) {
                                  document.body.style.overflow = 'hidden';
                                } else {
                                  document.body.style.overflow = 'unset';
                                }
                              },
                            }}
                          />
                        );
                      })}
                  </Image.PreviewGroup>
                  <div className={'swiper-lazy-preloader'} />
                  {status === 'SOLDOUT' && <Dimmed />}
                </CustomSwiperSlide>
              );
            })}
        </Swiper>

        {images && images.filter((image) => image.type !== 'LIST').length > 0 && (
          <ImageCounterCenterBottom
            currentPageNum={activeSlideImageIndex + 1}
            totalPageNum={images.filter((image) => image.type !== 'LIST').length}
          />
        )}
        {status === 'SOLDOUT' && (
          <>
            <DimmedCenterMessage1>{'SOLD'}</DimmedCenterMessage1>
            <DimmedCenterMessage2>{'OUT'}</DimmedCenterMessage2>
          </>
        )}
      </SwiperWrapper>
    );
  }

  return null;
};

const SwiperWrapper = styled.div`
  position: relative;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  position: relative;

  cursor: pointer;
`;

const NoAutoHeightImg = styled.img`
  width: 100vw;
  height: 100%;

  position: absolute;

  left: 0;
  top: 0;

  object-fit: cover;
`;

const Dimmed = styled.div`
  position: absolute;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  border-radius: var(--radius-medium, 8px);
  background: rgba(35, 36, 41, 0.6);
  backdrop-filter: blur(4px);
`;

const DimmedCenterMessage1 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% - 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;

const DimmedCenterMessage2 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% + 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;
